import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import Campaign from '../../../models/KB Layer/campaign';
import Universe from '../../../models/KB Layer/universe';
import { AuthService } from '../../../services/auth-service';
import { GamelayerService } from '../../../services/gamelayer-service';
import { KblayerService } from '../../../services/kblayer-service';
import { BackButtonComponent } from '../../back-button-component/back-button-component';
import { LoadingComponent } from '../../loading-component/loading-component';
import { CampaignCardComponent } from '../campaign-card-component/campaign-card-component';

@Component({
  selector: 'app-session-create-component',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    BackButtonComponent,
    LoadingComponent,
    CampaignCardComponent,
  ],
  templateUrl: './session-create-component.html',
  styleUrl: './session-create-component.css',
})
export class SessionCreateComponent {
  private readonly router = inject(Router);
  private readonly kblayerService = inject(KblayerService);
  private readonly gamelayerService = inject(GamelayerService);
  readonly auth = inject(AuthService);

  readonly universes = signal<Universe[]>([]);
  readonly sessionName = signal('');
  readonly sessionDescription = signal('');
  readonly selectedUniverseId = signal('');
  readonly selectedCampaignId = signal('');
  readonly isLoadingUniverses = signal(true);
  readonly isRecapOpen = signal(false);
  readonly isFinalizing = signal(false);
  readonly loadUniversesError = signal('');
  readonly createSessionError = signal('');

  readonly selectedUniverse = computed(
    () => this.universes().find((universe) => universe.id === this.selectedUniverseId()) ?? null,
  );
  readonly availableCampaigns = computed(() => this.selectedUniverse()?.campaigns ?? []);
  readonly selectedCampaign = computed(
    () =>
      this.availableCampaigns().find((campaign) => campaign.id === this.selectedCampaignId()) ??
      null,
  );

  ngOnInit(): void {
    this.loadUniverses();
  }

  loadUniverses(): void {
    this.isLoadingUniverses.set(true);
    this.loadUniversesError.set('');

    this.kblayerService.getUniverses().subscribe({
      next: (response) => {
        const rawUniverses = Array.isArray(response) ? response : response?.universes ?? [];

        this.universes.set(rawUniverses.map((universe) => new Universe(universe)));
        this.isLoadingUniverses.set(false);
      },
      error: (error) => {
        console.error('Error loading universes:', error);
        this.loadUniversesError.set('The available universes could not be loaded right now.');
        this.isLoadingUniverses.set(false);
      },
    });
  }

  onUniverseChange(universeId: string): void {
    this.selectedUniverseId.set(universeId);
    this.selectedCampaignId.set('');
    this.isRecapOpen.set(false);
    this.createSessionError.set('');
  }

  onCampaignChange(campaignId: string): void {
    this.selectedCampaignId.set(campaignId);
    this.isRecapOpen.set(false);
    this.createSessionError.set('');
  }

  updateSessionName(name: string): void {
    this.sessionName.set(name);
    this.isRecapOpen.set(false);
    this.createSessionError.set('');
  }

  updateSessionDescription(description: string): void {
    this.sessionDescription.set(description);
    this.isRecapOpen.set(false);
    this.createSessionError.set('');
  }

  chooseCampaign(campaign: Campaign): void {
    this.onCampaignChange(campaign.id);
  }

  canReviewSession(): boolean {
    return !!(
      this.sessionName().trim() &&
      this.sessionDescription().trim() &&
      this.selectedUniverse() &&
      this.selectedCampaign()
    );
  }

  openRecap(): void {
    if (!this.canReviewSession()) {
      this.createSessionError.set(
        'Enter a session name, session description, universe, and campaign before creating the session.',
      );
      return;
    }

    this.isRecapOpen.set(true);
    this.createSessionError.set('');
  }

  finalizeSession(): void {
    const currentUser = this.auth.currentUser();
    const universe = this.selectedUniverse();
    const campaign = this.selectedCampaign();

    if (!currentUser || !universe || !campaign || !this.canReviewSession()) {
      this.createSessionError.set(
        'Enter a session name, session description, universe, and campaign before finalizing the session.',
      );
      return;
    }

    this.isFinalizing.set(true);
    this.createSessionError.set('');

    const payload = {
      name: this.sessionName().trim(),
      description: this.sessionDescription().trim(),
      universeName: universe.name,
      universeId: universe.id,
      campaignName: campaign.name,
      campaignId: campaign.id,
      masterId: currentUser.id,
      maxPlayers: 4,
      players: [],
    };

    this.gamelayerService.postSession(payload).subscribe({
      next: () => {
        this.isFinalizing.set(false);
        void this.router.navigate(['/sessions']);
      },
      error: (error) => {
        console.error('Error creating session:', error);
        this.createSessionError.set('The session could not be created right now.');
        this.isFinalizing.set(false);
      },
    });
  }
}
